<template>
  <div class="content">
    <!-- 顶部导航栏 -->
    <div class="top-bar">
      <div class="top-bar__left">
        <div class="top-bar__logo" @click="switchCollapse">
          <img src="@/assets/logo.png" alt="" class="top-bar__logo-img">
          <span class="top-bar__logo-text">应用中心</span>
        </div>
        <span v-if="userData.isAdmin" class="top-bar__link" @click="switchMode">前往设计</span>
        <el-input
          placeholder="搜索菜单、功能..."
          v-model="searchMenuVal"
          @clear="clearFilterMenu"
          clearable
          prefix-icon="el-icon-search"
          class="top-bar__search"
          size="small"
          @keyup.enter.native="filterMenu"
        />
      </div>
      <div class="top-bar__right">
        <div v-if="sysInfoModel.name" class="top-bar__hospital">
          <el-tooltip :content="hasInsurance ? '医保服务已连接' : '医保服务未连接'" placement="bottom">
            <span :class="['top-bar__status-dot', { 'top-bar__status-dot--error': !hasInsurance }]"></span>
          </el-tooltip>
          <el-tooltip placement="bottom" effect="dark" :disabled="!hospitalTooltipContent">
            <template slot="content"><div v-html="hospitalTooltipContent"></div></template>
            <span class="top-bar__hospital-name">{{ sysInfoModel.name }}</span>
          </el-tooltip>
          <el-tooltip :content="lastScheduleSyncTimeText" placement="bottom" :open-delay="300">
            <el-button type="primary" plain size="mini" icon="el-icon-refresh" :loading="hdisScheduleSyncing" style="margin-left: 12px;" @click.stop="syncHdisSchedule">同步排班</el-button>
          </el-tooltip>
        </div>
        <!-- 通用站内信铃铛 -->
        <notification-bell class="top-bar__notification" />
        <div class="top-bar__user">
          <div class="top-bar__avatar"><i class="el-icon-user-solid"></i></div>
          <el-dropdown trigger="click" @command="handleUserCommand">
            <div class="top-bar__user-info">
              <span class="top-bar__user-name">{{ userData.name || '未知' }}</span>
              <span class="top-bar__user-role">{{ userData.isAdmin ? '系统管理员' : '' }}</span>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">我的资料</el-dropdown-item>
              <el-dropdown-item command="workOrder">系统工单</el-dropdown-item>
              <el-dropdown-item command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </div>
    <div class="body">
      <!-- 左侧菜单 -->
      <div class="sidebar">
        <el-skeleton class="skeleton" v-if="loading" :rows="16" animated />
        <el-menu
          v-if="!loading"
          :default-active="menuActiveIndex"
          :default-openeds="defaultOpeneds"
          :collapse="isCollapse"
          class="sidebar-menu"
          :unique-opened="false">
          <template v-for="menu in menuList">
            <!-- type=1, mode=0: 有流程分组的应用 -->
            <el-submenu :index="menu.id" :key="menu.id" v-if="menu.type == 1 && menu.mode == 0">
              <template slot="title">
                <i :class="getMenuIcon(menu.name)" class="menu-icon"></i>
                <span v-if="!isCollapse">{{ menu.name }}</span>
              </template>
              <el-menu-item-group v-for="group in menu.items" :key="group.id">
                <template slot="title">{{ group.name }}</template>
                <div class="menu-null" v-if="!group.items || group.items.length < 1">该流程暂无表单</div>
                <el-menu-item v-for="item in group.items" :key="item.id" :index="item.id" @click="jumpApp(item)">
                  <span>{{ item.name }}</span>
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
            <!-- type=1, mode=1: 无流程的应用（直接子菜单） -->
            <el-submenu :index="menu.id" :key="menu.id" v-else-if="menu.type == 1 && menu.mode == 1">
              <template slot="title">
                <i :class="getMenuIcon(menu.name)" class="menu-icon"></i>
                <span v-if="!isCollapse">{{ menu.name }}</span>
              </template>
              <div class="menu-null" v-if="!menu.items || menu.items.length < 1">该应用暂无表单</div>
              <el-menu-item v-for="item in menu.items" :key="item.id" :index="item.id" @click="jumpApp(item)">
                <span>{{ item.name }}</span>
              </el-menu-item>
            </el-submenu>
            <!-- type=10: 单个报表 -->
            <el-menu-item v-else-if="menu.type == 10" :key="menu.id" :index="menu.id" @click="jumpApp(menu)">
              <i class="el-icon-tickets menu-icon"></i>
              <span slot="title">{{ menu.name }}</span>
            </el-menu-item>
            <!-- type=11: 报表分类 -->
            <el-submenu :index="menu.id" :key="menu.id" v-else-if="menu.type == 11">
              <template slot="title">
                <i class="el-icon-s-data menu-icon"></i>
                <span v-if="!isCollapse">{{ menu.name }}</span>
              </template>
              <div class="menu-null" v-if="!menu.items || menu.items.length < 1">该类别暂无报表</div>
              <template v-for="sub in menu.items">
                <el-menu-item v-if="sub.type == 10" :key="sub.id" :index="sub.id" @click="jumpApp(sub)">
                  <span>{{ sub.name }}</span>
                </el-menu-item>
                <el-submenu v-else-if="sub.type == 11" :key="sub.id" :index="sub.id" class="nested-submenu">
                  <template slot="title"><i class="el-icon-s-data menu-icon"></i>{{ sub.name }}</template>
                  <div class="menu-null" v-if="!sub.items || sub.items.length < 1">该类别暂无报表</div>
                  <el-menu-item v-for="leaf in sub.items" :key="leaf.id" :index="leaf.id" @click="jumpApp(leaf)">
                    <span>{{ leaf.name }}</span>
                  </el-menu-item>
                </el-submenu>
              </template>
            </el-submenu>
          </template>
          <el-empty :image-size="25" class="empty" v-if="!loading && menuList.length < 1" description="暂无菜单"></el-empty>
        </el-menu>
        <!-- 底部版本信息 -->
        <div v-if="!isCollapse" class="sidebar-footer">
          <span>版本 v1.0.022</span>
          <span class="sidebar-footer__help">帮助中心</span>
        </div>
      </div>
      <div class="main">
        <router-view v-if="!currExtData && !routeLoading" :key="$route.fullPath" /> 
        <el-skeleton class="skeleton" v-if="extloading || routeLoading" :rows="16" animated />
        <iframe v-if="currExtData" class="ext" :src="currExtData.path" frameborder="0"></iframe>
      </div>
    </div>
    <el-dialog  
        title="用户资料"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        width="600px"
        :visible.sync="userInfoVisible">
        <div>
            <user-info />
        </div>
    </el-dialog>
    <el-dialog  
        title="Ai问答"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        width="1200px"
        :visible.sync="aiVisbile">
        <div>
            <ai-chat />
        </div>
    </el-dialog>
    <!-- 发起工单弹框 -->
    <!-- 工单列表弹框 -->
    <el-dialog title="工单" :visible.sync="workOrderListVisible" width="900px" :close-on-click-modal="false" append-to-body>
      <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
        <div>
          <el-radio-group v-model="workOrderListFilter" size="small" @change="loadWorkOrderList">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button label="0">待处理</el-radio-button>
            <el-radio-button label="1">处理中</el-radio-button>
            <el-radio-button label="3">已关闭</el-radio-button>
          </el-radio-group>
        </div>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="showWorkOrderForm">发起工单</el-button>
      </div>
      <el-table :data="workOrderListData" v-loading="workOrderListLoading" stripe border size="small" @row-click="viewWorkOrderDetail">
        <el-table-column label="标题" prop="title" min-width="200" show-overflow-tooltip />
        <el-table-column label="提交人" prop="creatorUserName" width="100" />
        <el-table-column label="标签" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.tag === 0" type="danger" size="mini">系统Bug</el-tag>
            <el-tag v-else-if="row.tag === 1" type="warning" size="mini">功能建议</el-tag>
            <el-tag v-else-if="row.tag === 2" size="mini">使用咨询</el-tag>
            <el-tag v-else type="info" size="mini">其他</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.status === 0" type="warning" size="mini">待处理</el-tag>
            <el-tag v-else-if="row.status === 1" type="" size="mini">处理中</el-tag>
            <el-tag v-else-if="row.status === 2" type="success" size="mini">已处理</el-tag>
            <el-tag v-else-if="row.status === 3" type="info" size="mini">已关闭</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" width="150">
          <template slot-scope="{ row }">{{ formatDateTime(row.creationTime) }}</template>
        </el-table-column>
      </el-table>
      <div style="margin-top: 12px; padding-bottom: 10px; text-align: right;">
        <el-pagination layout="total, prev, pager, next" :total="workOrderListTotal" :page-size="10" :current-page.sync="workOrderListPage" @current-change="loadWorkOrderList" />
      </div>
    </el-dialog>
    <!-- 工单详情弹框 -->
    <el-dialog :title="'工单详情 - ' + (workOrderDetail.title || '')" :visible.sync="workOrderDetailVisible" width="750px" :close-on-click-modal="false" append-to-body>
      <div v-loading="workOrderDetailLoading" style="padding-bottom: 10px;">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="提交人">{{ workOrderDetail.creatorUserName }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag v-if="workOrderDetail.status === 0" type="warning" size="mini">待处理</el-tag>
            <el-tag v-else-if="workOrderDetail.status === 1" size="mini">处理中</el-tag>
            <el-tag v-else-if="workOrderDetail.status === 2" type="success" size="mini">已处理</el-tag>
            <el-tag v-else-if="workOrderDetail.status === 3" type="info" size="mini">已关闭</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="标签">
            <el-tag v-if="workOrderDetail.tag === 0" type="danger" size="mini">系统Bug</el-tag>
            <el-tag v-else-if="workOrderDetail.tag === 1" type="warning" size="mini">功能建议</el-tag>
            <el-tag v-else-if="workOrderDetail.tag === 2" size="mini">使用咨询</el-tag>
            <el-tag v-else type="info" size="mini">其他</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatDateTime(workOrderDetail.creationTime) }}</el-descriptions-item>
          <el-descriptions-item label="工单内容" :span="2">
            <div v-html="workOrderDetail.content" style="line-height: 1.6;" />
          </el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.remark" label="备注" :span="2">{{ workOrderDetail.remark }}</el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.traceId" label="TraceId" :span="2">
            <code>{{ workOrderDetail.traceId }}</code>
          </el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.resolution" label="处理方案" :span="2">{{ workOrderDetail.resolution }}</el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.claimedByUserName" label="认领人">{{ workOrderDetail.claimedByUserName }}</el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.claimedTime" label="认领时间">{{ formatDateTime(workOrderDetail.claimedTime) }}</el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.resolvedByUserName" label="处理人">{{ workOrderDetail.resolvedByUserName }}</el-descriptions-item>
          <el-descriptions-item v-if="workOrderDetail.resolvedTime" label="处理时间">{{ formatDateTime(workOrderDetail.resolvedTime) }}</el-descriptions-item>
        </el-descriptions>
        <div v-if="(workOrderDetail.images && workOrderDetail.images.length) || workOrderDetail.screenshotUrl" style="margin-top: 12px;">
          <div style="font-weight: bold; margin-bottom: 8px;">截图</div>
          <div style="display: flex; flex-wrap: wrap; gap: 8px;">
            <el-image v-for="img in (workOrderDetail.images || [])" :key="img.id" :src="img.imageUrl" :preview-src-list="(workOrderDetail.images || []).map(i => i.imageUrl)" style="width: 150px; height: 120px; border: 1px solid #ebeef5; border-radius: 4px; object-fit: cover;" fit="cover" />
            <el-image v-if="workOrderDetail.screenshotUrl && !(workOrderDetail.images && workOrderDetail.images.length)" :src="getFullUrl(workOrderDetail.screenshotUrl)" :preview-src-list="[getFullUrl(workOrderDetail.screenshotUrl)]" style="width: 150px; height: 120px; border: 1px solid #ebeef5; border-radius: 4px;" fit="cover" />
          </div>
        </div>
      </div>
    </el-dialog>
    <el-dialog
        title="发起工单"
        :close-on-click-modal="false"
        width="700px"
        :visible.sync="workOrderFormVisible"
        @close="resetWorkOrderForm">
        <el-form ref="workOrderForm" :model="workOrderForm" :rules="workOrderRules" label-width="100px">
          <el-form-item label="工单标题" prop="title">
            <el-input v-model="workOrderForm.title" placeholder="请输入工单标题" maxlength="200" show-word-limit />
          </el-form-item>
          <el-form-item label="工单标签" prop="tag">
            <el-radio-group v-model="workOrderForm.tag">
              <el-radio-button :label="0">系统Bug</el-radio-button>
              <el-radio-button :label="1">数据异常</el-radio-button>
              <el-radio-button :label="2">功能缺失</el-radio-button>
              <el-radio-button :label="3">用户操作不当</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="工单内容" prop="content">
            <el-input v-model="workOrderForm.content" type="textarea" :rows="5" placeholder="请描述问题详情" />
          </el-form-item>
          <el-form-item label="截图（最多5张）">
            <el-upload
              action=""
              :http-request="handleImageUpload"
              list-type="picture-card"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              :before-upload="beforeScreenshotUpload"
              :file-list="workOrderForm.imageFileList"
              :on-remove="handleImageRemove"
              :limit="5"
              :on-exceed="() => $message.warning('最多上传5张截图')"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="workOrderForm.remark" type="textarea" :rows="2" placeholder="选填" />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="workOrderFormVisible = false">取消</el-button>
          <el-button type="primary" :loading="workOrderSubmitting" @click="submitWorkOrder">提交</el-button>
        </div>
    </el-dialog>
  </div>
</template>

<script> 
import UserInfo from '@/components/users/UserInfo.vue';
import NotificationBell from '@/components/notification/NotificationBell.vue';

import AiChat from './dialogs/AiChatDialog.vue';
import { clearCache, getLocalUser } from '@/services/user.service';
import { getMenu } from '@/services/app.service'; 
import { getHospital } from '@/services/inst.service';
import { getHisStatus, startHisHeartbeat, stopHisHeartbeat } from '@/services/his-status.service';
import { hdisListPatients } from '@/services/his.hdis';
import { hisSyncFromHdis, hisGetLatestScheduleSyncTime } from '@/services/his.patient';
import authrequest from '@/utils/authrequest';
import { setMenuAccess, isReadonlyPath } from '@/utils/menu-access';
import { syncReadonlyGuard, stopReadonlyGuard } from '@/utils/readonly-guard';
import dayjs from 'dayjs';
export default {
  name: 'AppView',
  components: {
    UserInfo,
    AiChat,
    NotificationBell
  },
  created(){
      this.userData = getLocalUser();
  },
  data() {
    return {
      sysInfoModel: {name: ''},
      hospitalInfo: null,
      userInfoVisible: false,
      aiVisbile: false,
      menuActiveIndex: '',
      defaultOpeneds: [],
      isCollapse: false,
      extloading: false,
      loading: false,
      routeLoading: false,
      menuList: [],
      allMenuList: '', // 菜单原始数据的 JSON 字符串，用于搜索重置
      currExtData: undefined,
      searchMenuVal: '',
      pathToItemId: {},   // 路由路径 -> 菜单项 id 的映射
      itemIdToParentId: {}, // 菜单项 id -> 父级菜单 id 的映射
      hdisScheduleSyncing: false,
      lastScheduleSyncTime: null,
      // 工单表单
      workOrderFormVisible: false,
      workOrderSubmitting: false,
      screenshotUploading: false,
      workOrderId: '',
      // 工单列表
      workOrderListVisible: false,
      workOrderListLoading: false,
      workOrderListData: [],
      workOrderListTotal: 0,
      workOrderListPage: 1,
      workOrderListFilter: '',
      // 工单详情
      workOrderDetailVisible: false,
      workOrderDetailLoading: false,
      workOrderDetail: {},
      // 工单表单
      workOrderForm: {
        title: '',
        tag: 0,
        content: '',
        remark: '',
        imageUrls: [],
        imageFileList: []
      },
      workOrderRules: {
        title: [{ required: true, message: '请输入工单标题', trigger: 'blur' }],
        tag: [{ required: true, message: '请选择工单标签', trigger: 'change' }],
        content: [{ required: true, message: '请输入工单内容', trigger: 'blur' }]
      }
    };
  },
  watch: {
    // 监听路由路径变化，同步菜单激活状态
    '$route.path': {
      handler() {
        // 路由变化时清空 iframe 扩展数据
        if (this.currExtData) {
          this.currExtData = undefined;
        }
        this.syncMenuActive();
        this.syncReadonlyState();
      }
    }
  },
  mounted(){
      this.getAppMenu();
      this.loadHospitalInfo();
      this.loadLastScheduleSyncTime();
      // 启动医保（EasyHis）连接探活：登录进入 AppView 后立即探一次 + 3 分钟心跳
      startHisHeartbeat();
  },
  beforeDestroy(){
      stopReadonlyGuard();
      stopHisHeartbeat();
  },
  computed: {
    /**
     * 医保服务连接状态（驱动顶栏红绿点）
     * - online / unknown：显示绿点（unknown 是首次心跳尚未回来的瞬时态，倾向放行）
     * - offline：显示红点
     */
    hasInsurance() {
      if (!this.hospitalInfo || !this.hospitalInfo.hisLocalServiceUrl) return false;
      return getHisStatus().status !== 'offline';
    },
    hospitalTooltipContent() {
      if (!this.hospitalInfo) return '';
      return this.hasInsurance ? '' : '医保服务未连接';
    },
    lastScheduleSyncTimeText() {
      if (!this.lastScheduleSyncTime) {
        return '尚未同步过排班信息';
      }
      return '最后同步时间：' + dayjs(this.lastScheduleSyncTime).format('YYYY-MM-DD HH:mm:ss');
    },
    
  },
  methods: {
    loadHospitalInfo(){
      const res = getHospital();
      this.hospitalInfo = res || null;
      this.sysInfoModel.name = res ? res.name : '';
    },
    /** 一键同步所有 HDIS 患者排班信息 */
    async syncHdisSchedule() {
      this.hdisScheduleSyncing = true;
      try {
        let allPatients = [];
        let pageIndex = 1;
        const pageSize = 200;
        let hasMore = true;
        while (hasMore) {
          const res = await hdisListPatients({ pageIndex, pageSize });
          const items = Array.isArray(res) ? res : [];
          allPatients = allPatients.concat(items);
          hasMore = items.length >= pageSize;
          pageIndex++;
        }
        if (allPatients.length === 0) {
          this.$message.warning('HDIS 无患者数据');
          return;
        }
        const syncItems = allPatients.map(row => ({
          hdisPatientId: row.id || '',
          patientName: row.patientName || '',
          patientSex: row.patientSex || '',
          patientBirthday: row.patientBirthday || null,
          cardNum: row.cardNum || '',
          dialysisId: row.dialysisId || '',
          contactPhone: row.contactPhone || '',
          mailingAddress: row.mailingAddress || '',
          scheduleClassName: row.scheduleClassName || '',
          scheduleClassTime: row.scheduleClassTime || '',
          treatmentMode: row.treatmentMode || '',
          areaName: row.areaName || ''
        }));
        const result = await hisSyncFromHdis({ items: syncItems });
        this.$message.success(`排班同步完成：新增 ${result.createdCount || 0} 人，更新 ${result.updatedCount || 0} 人`);
        this.loadLastScheduleSyncTime();
      } catch (e) {
        console.error('同步排班失败:', e);
        this.$message.error('同步排班失败');
      } finally {
        this.hdisScheduleSyncing = false;
      }
    },
    /** 获取最晚的排班同步时间 */
    async loadLastScheduleSyncTime() {
      try {
        const res = await hisGetLatestScheduleSyncTime();
        this.lastScheduleSyncTime = res || null;
      } catch (e) {
        // 静默失败，不影响主流程
      }
    },
    showAiChat(){
      this.aiVisbile = true;
    }, 
    /** 获取菜单数据并构建路径映射 */
    async getAppMenu() {
      this.loading = true;
      try {
        const res = await getMenu();
        this.menuList = res || [];
        this.allMenuList = JSON.stringify(this.menuList);
        this.buildMenuMaps(this.menuList);
        // 建立「路由路径 -> 访问级别」映射，供只读守卫和写请求兜底拦截使用
        setMenuAccess(this.menuList);
        this.syncMenuActive();
        this.syncReadonlyState();
      } catch (error) {
        console.error('加载菜单失败:', error);
      } finally {
        this.loading = false;
      }
    },
    /** 构建路径到菜单ID的映射表，一次遍历后续 O(1) 查找 */
    buildMenuMaps(menuList) {
      const pathMap = {};
      const parentMap = {};
      const walk = (items, parentId) => {
        if (!items) return;
        for (const item of items) {
          if (parentId) parentMap[item.id] = parentId;
          if (item.type == 1 && item.items) {
            // 顶级分组或流程分组，递归子项
            if (item.mode == 0) {
              // 有流程分组：items 是流程组，再递归组内子项
              for (const group of item.items) {
                walk(group.items, item.id);
              }
            } else {
              walk(item.items, item.id);
            }
          } else if (item.type == 11 && item.items) {
            // 报表分类，递归子项
            walk(item.items, item.id);
          } else {
            // 叶子菜单项，记录路径映射
            const path = this.resolveItemPath(item);
            if (path) pathMap[path] = item.id;
          }
        }
      };
      walk(menuList, null);
      this.pathToItemId = pathMap;
      this.itemIdToParentId = parentMap;
    },
    /** 根据菜单项类型解析出路由路径 */
    resolveItemPath(item) {
      if (!item) return '';
      if (item.type == 10) return '/index/report/' + item.id;
      if (item.type == 20) return item.path || '';
      if (item.code) return '/index/' + item.code;
      if (item.path) return item.path;
      return '/index/app/' + item.id;
    },
    /** 按当前路由的授权级别开关只读守卫（只读时自动置灰页面内的写操作控件） */
    syncReadonlyState() {
      syncReadonlyGuard(isReadonlyPath(this.$route.path));
    },
    /** 根据当前路由路径同步菜单激活状态，简单的 map 查找 */
    syncMenuActive() {
      const path = this.$route.path;
      const itemId = this.pathToItemId[path];
      if (!itemId) return;
      this.menuActiveIndex = itemId;
      // 构建需要展开的父级菜单链
      const openeds = [];
      let id = itemId;
      while (this.itemIdToParentId[id]) {
        id = this.itemIdToParentId[id];
        openeds.unshift(id);
      }
      this.defaultOpeneds = openeds;
    },
    /** 根据菜单名称返回对应图标 */
    getMenuIcon(name) {
      const iconMap = {
        '患者': 'el-icon-user',
        '医生': 'el-icon-s-custom',
        '库存': 'el-icon-box',
        '基础': 'el-icon-setting',
        '对账': 'el-icon-document',
        '收费': 'el-icon-wallet',
        '报表': 'el-icon-s-data',
        '统计': 'el-icon-s-data',
      };
      for (const key in iconMap) {
        if (name && name.includes(key)) return iconMap[key];
      }
      return 'el-icon-menu';
    },
    /** 菜单搜索清空 */
    clearFilterMenu() {
      this.menuList = JSON.parse(this.allMenuList);
      this.buildMenuMaps(this.menuList);
      this.syncMenuActive();
    },
    /** 菜单搜索过滤 */
    filterMenu() {
      const keyword = this.searchMenuVal.trim();
      if (!keyword) {
        this.clearFilterMenu();
        return;
      }
      const all = JSON.parse(this.allMenuList);
      // 过滤：父级名称匹配则保留全部子项，否则只保留匹配的子项
      this.menuList = all.filter(menu => {
        if (menu.name.includes(keyword)) return true;
        if (menu.items && menu.items.length > 0) {
          menu.items = menu.items.filter(item => item.name.includes(keyword));
          return menu.items.length > 0;
        }
        return false;
      });
      this.buildMenuMaps(this.menuList);
    },
    showUserInfo() {
      this.userInfoVisible = true;
    },
    /** 处理用户下拉菜单命令 */
    handleUserCommand(command) {
      if (command === 'profile') this.showUserInfo();
      else if (command === 'workOrder') this.showWorkOrderList();
      else if (command === 'logout') this.logout();
    },
    /** 菜单项点击跳转 */
    jumpApp(item) {
      if (!item) return;
      // dumpMode=1: 新窗口打开外部链接
      if (item.type == 20 && item.dumpMode == 1) {
        window.open(item.path, '_blank');
        return;
      }
      // dumpMode=0: iframe 嵌入
      if (item.type == 20 && item.dumpMode == 0) {
        this.currExtData = { path: item.path };
        this.menuActiveIndex = item.id;
        return;
      }
      const path = this.resolveItemPath(item);
      if (!path) {
        this.$message.warning('该菜单未配置路由');
        return;
      }
      // 清除 iframe
      if (this.currExtData) {
        this.currExtData = undefined;
      }
      // 已在当前路由则不重复跳转
      if (this.$route.path === path) return;
      // 立即设置激活状态，避免闪烁
      this.menuActiveIndex = item.id;
      this.$router.push(path).catch(() => {});
    },
    switchMode() {
      this.$router.push('/home');
    },
    switchCollapse() {
      this.isCollapse = !this.isCollapse;
    },
    logout() {
      clearCache();
      this.$router.push('/login');
    },
    /** 生成UUID */
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },
    /** 格式化日期时间 */
    formatDateTime(val) {
      if (!val) return '-';
      return new Date(val).toLocaleString('zh-CN');
    },
    /** 获取完整URL（兼容COS和相对路径） */
    getFullUrl(url) {
      if (!url) return '';
      if (url.startsWith('http')) return url;
      const baseUrl = process.env.NODE_ENV === 'production' ? window.globalConfig.rootUrl : process.env.VUE_APP_BASE_URL;
      return baseUrl + url;
    },
    /** 显示工单列表弹框 */
    showWorkOrderList() {
      this.workOrderListVisible = true;
      this.workOrderListPage = 1;
      this.workOrderListFilter = '';
      this.loadWorkOrderList();
    },
    /** 加载工单列表 */
    async loadWorkOrderList() {
      this.workOrderListLoading = true;
      try {
        const params = { SkipCount: (this.workOrderListPage - 1) * 10, MaxResultCount: 10 };
        if (this.workOrderListFilter !== '') params.Status = parseInt(this.workOrderListFilter);
        const res = await authrequest({ url: '/api/app/work-order/my-list', method: 'get', params });
        this.workOrderListData = res.items || [];
        this.workOrderListTotal = res.totalCount || 0;
      } catch (err) {
        this.$message.error('加载工单列表失败');
      } finally {
        this.workOrderListLoading = false;
      }
    },
    /** 查看工单详情 */
    async viewWorkOrderDetail(row) {
      this.workOrderDetailVisible = true;
      this.workOrderDetailLoading = true;
      try {
        this.workOrderDetail = await authrequest({ url: `/api/app/work-order/${row.id}`, method: 'get' });
      } catch (err) {
        this.$message.error('加载工单详情失败');
      } finally {
        this.workOrderDetailLoading = false;
      }
    },
    /** 显示工单表单弹框 */
    showWorkOrderForm() {
      this.workOrderId = this.generateUUID();
      this.workOrderFormVisible = true;
    },
    /** 重置工单表单 */
    resetWorkOrderForm() {
      this.workOrderForm = { title: '', tag: 0, content: '', remark: '', imageUrls: [], imageFileList: [] };
      this.workOrderId = '';
      this.screenshotUploading = false;
      if (this.$refs.workOrderForm) {
        this.$refs.workOrderForm.resetFields();
      }
    },
    /** 截图上传前校验 */
    beforeScreenshotUpload(file) {
      const isImage = /\.(jpe?g|png|gif|webp)$/i.test(file.name);
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isImage) { this.$message.error('仅支持 jpg/png/gif/webp 格式的图片'); return false; }
      if (!isLt5M) { this.$message.error('图片大小不能超过5MB'); return false; }
      return true;
    },
    /** 多图上传 */
    async handleImageUpload({ file }) {
      this.screenshotUploading = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const url = await authrequest({ url: `/api/app/upload/image?workOrderId=${this.workOrderId}`, method: 'post', data: formData });
        this.workOrderForm.imageUrls.push(url);
        this.workOrderForm.imageFileList.push({ name: file.name, url: url.startsWith('http') ? url : (process.env.NODE_ENV === 'production' ? window.globalConfig.rootUrl : process.env.VUE_APP_BASE_URL) + url });
      } catch (err) {
        this.$message.error('截图上传失败: ' + (err.message || '未知错误'));
      } finally {
        this.screenshotUploading = false;
      }
    },
    /** 删除已上传截图 */
    handleImageRemove(file) {
      const idx = this.workOrderForm.imageFileList.findIndex(f => f.url === file.url);
      if (idx > -1) {
        this.workOrderForm.imageFileList.splice(idx, 1);
        this.workOrderForm.imageUrls.splice(idx, 1);
      }
    },
    /** 提交工单 */
    async submitWorkOrder() {
      try { await this.$refs.workOrderForm.validate(); } catch { return; }
      this.workOrderSubmitting = true;
      try {
        await authrequest({
          url: '/api/app/work-order',
          method: 'post',
          data: {
            id: this.workOrderId || undefined,
            title: this.workOrderForm.title,
            tag: this.workOrderForm.tag,
            content: this.workOrderForm.content,
            remark: this.workOrderForm.remark,
            imageUrls: this.workOrderForm.imageUrls.length ? this.workOrderForm.imageUrls : undefined
          }
        });
        this.$message.success('工单提交成功');
        this.workOrderFormVisible = false;
        if (this.workOrderListVisible) this.loadWorkOrderList();
      } catch (err) {
        this.$message.error('工单提交失败: ' + (err.message || '未知错误'));
      } finally {
        this.workOrderSubmitting = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* ========== 全局布局 ========== */
.content {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f5f7fa;
}

/* ========== 顶部导航栏 ========== */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 20px;
  background: #fff;
  border-bottom: 1px solid #e8ecf0;
  flex-shrink: 0;
  z-index: 100;
}
.top-bar__left {
  display: flex;
  align-items: center;
  gap: 20px;
}
.top-bar__logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 8px;
}
.top-bar__logo-img {
  width: 34px;
  height: 34px;
}
.top-bar__logo-text {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  white-space: nowrap;
}
.top-bar__link {
  color: #409EFF;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.8; }
}
.top-bar__search {
  width: 260px;
  ::v-deep .el-input__inner {
    border-radius: 20px;
    background: #f5f7fa;
    border-color: #e8ecf0;
    &:focus { background: #fff; }
  }
}
.top-bar__right {
  display: flex;
  align-items: center;
  gap: 20px;
}
.top-bar__hospital {
  display: flex;
  align-items: center;
  gap: 8px;
}
.top-bar__status-dot {
  width: 8px;
  height: 8px;
  background: #67c23a;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
  &--error { background: #f56c6c; }
}
.top-bar__hospital-name {
  font-size: 14px;
  color: #303133;
  cursor: pointer;
  white-space: nowrap;
}
.top-bar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.top-bar__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #e8ecf0;
  display: flex;
  align-items: center;
  justify-content: center;
  i { font-size: 18px; color: #909399; }
}
.top-bar__user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.top-bar__user-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}
.top-bar__user-role {
  font-size: 11px;
  color: #909399;
}
/* 下拉箭头垂直居中 */
.top-bar__user ::v-deep .el-dropdown {
  display: flex;
  align-items: center;
}

/* ========== 主体区域 ========== */
.body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* ========== 左侧菜单栏 ========== */
.sidebar {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e8ecf0;
  overflow: hidden;

  .skeleton {
    width: 240px;
    flex: 1;
    padding: 16px 12px;
    box-sizing: border-box;
  }
}
.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: none !important;

  &:not(.el-menu--collapse) {
    width: 240px;
  }
}
/* 菜单图标 */
.menu-icon {
  font-size: 20px;
  color: #909399;
  margin-right: 10px !important;
  flex-shrink: 0;
}
/* 顶级菜单标题行 */
.sidebar-menu ::v-deep .el-submenu__title {
  display: flex;
  align-items: center;
  height: 52px;
  font-size: 15px;
  font-weight: 500;
  color: #303133;
  padding-left: 16px !important;
  margin: 2px 8px;
  border-radius: 8px;
  .el-submenu__icon-arrow { margin-top: 0; }
}
/* 激活的顶级菜单 — 整行高亮 */
.sidebar-menu ::v-deep .el-submenu.is-active > .el-submenu__title {
  background: #ecf5ff;
  color: #409EFF;
  .menu-icon { color: #409EFF; }
}
/* 子菜单项 */
.sidebar-menu ::v-deep .el-menu-item {
  height: 44px;
  line-height: 44px;
  font-size: 14px;
  color: #606266;
  padding-left: 52px !important;
  margin: 2px 8px;
  border-radius: 8px;
  &:hover {
    background: #f5f7fa;
  }
  &.is-active {
    color: #409EFF;
    font-weight: 500;
    background: transparent;
  }
}
/* 子菜单容器去掉默认背景 */
.sidebar-menu ::v-deep .el-menu--inline {
  background: transparent !important;
}
/* 二级嵌套 submenu — 弱化为次级层级，与一级拉开主次 */
.sidebar-menu ::v-deep .nested-submenu > .el-submenu__title {
  height: 40px;
  font-size: 14px;
  font-weight: 400;
  color: #606266;
  padding-left: 36px !important;
  margin: 1px 8px;
  .menu-icon {
    font-size: 14px;
    color: #c0c4cc;
    margin-right: 8px !important;
  }
}
.sidebar-menu ::v-deep .nested-submenu.is-active > .el-submenu__title {
  background: transparent;
  color: #409EFF;
  font-weight: 500;
  .menu-icon { color: #409EFF; }
}
/* 二级 submenu 下的叶子项再缩进一层 */
.sidebar-menu ::v-deep .nested-submenu .el-menu-item {
  padding-left: 64px !important;
}
.menu-null {
  height: 36px;
  line-height: 36px;
  font-size: 12px;
  color: #c0c4cc;
  padding-left: 52px;
}
/* 侧栏底部版本信息 */
.sidebar-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  font-size: 12px;
  color: #c0c4cc;
  border-top: 1px solid #e8ecf0;
  flex-shrink: 0;
}
.sidebar-footer__help {
  color: #909399;
  cursor: pointer;
  &:hover { color: #409EFF; }
}

/* ========== 主内容区 ========== */
.main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  background: #f5f7fa;
  padding: 0;

  .skeleton {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    padding: 20px;
    box-sizing: border-box;
  }
}
.main::-webkit-scrollbar {
  width: 6px;
  height: 6px;
  background-color: transparent;
}
.main::-webkit-scrollbar-thumb {
  background-color: #c0c4cc;
  border-radius: 3px;
}

.ext {
  width: 100%;
  height: 100%;
  overflow: auto;
}

/* ========== 全局滚动条 ========== */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
  background-color: transparent;
}
::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background-color: #c0c4cc;
}
</style>
